import { useState, useEffect, useCallback, Suspense } from 'react';
import pageMeta from './data/pageMeta.json';
import Header from './components/Header';
import Footer from './components/Footer';
import ArticleFooter from './components/ArticleFooter';
import { PAGE_COMPONENTS } from './routes';

// ===== ページメタ情報（タイトル・説明文は src/data/pageMeta.json で管理） =====
const PAGE_TITLES = pageMeta.titles;
const DEFAULT_DESCRIPTION = pageMeta.defaultDescription;
const PAGE_DESCRIPTIONS = pageMeta.descriptions;

function updateMeta(page) {
  const title = PAGE_TITLES[page] || '臨床ナースラボ';
  const description = PAGE_DESCRIPTIONS[page] || DEFAULT_DESCRIPTION;
  // 正規URLは末尾スラッシュ付きに統一する（Netlifyが /page → /page/ に転送するため、サイトマップとも揃える）
  const url = page === 'home' ? 'https://rinshonurselab.com/' : 'https://rinshonurselab.com/' + page + '/';

  document.title = title;

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', url);

  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', description);

  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', title);

  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc) ogDesc.setAttribute('content', description);

  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute('content', url);

  const twTitle = document.querySelector('meta[name="twitter:title"]');
  if (twTitle) twTitle.setAttribute('content', title);

  const twDesc = document.querySelector('meta[name="twitter:description"]');
  if (twDesc) twDesc.setAttribute('content', description);
}

// ===== メインアプリ =====
export default function App() {
  const getPageFromURL = () => {
    // パスベース: /article_design_r → article_design_r
    // 末尾スラッシュ付きURL（/article_enema/ など）でもページを正しく判定する
    const path = window.location.pathname.slice(1).replace(/\/+$/, '');
    if (path && !path.includes('.')) return path;
    // クエリパラメータ: ?page=article_design_r
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('page');
    if (fromQuery) return fromQuery;
    // ハッシュ: #article_design_r
    const hash = window.location.hash.slice(1);
    return hash || 'home';
  };

  const [currentPage, setCurrentPageState] = useState(getPageFromURL);

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(page);
    const url = page === 'home' ? '/' : '/' + page;
    window.history.pushState(null, '', url);
    updateMeta(page);
    window.scrollTo(0, 0);
  }, []);

  // 初回アクセス時のメタ情報設定
  useEffect(() => {
    const initialPage = getPageFromURL();
    updateMeta(initialPage);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const page = getPageFromURL();
      setCurrentPageState(page);
      updateMeta(page);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const PageComponent = PAGE_COMPONENTS[currentPage] || PAGE_COMPONENTS.home;
  // 記事ページには末尾に共通の「関連ツール・あわせて読みたい・書いた人」ブロックを付ける
  const isArticle = currentPage.startsWith('article_') && PAGE_COMPONENTS[currentPage];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <main className="flex-1">
        {/* 記事・ツールページは遅延読み込みのため、読み込み中はSuspenseで待つ */}
        {/* ※ scripts/prerender.js が「<main> の直下に Suspense が1つ」という前提で hydration 用の目印を入れている。構造を変えるときは prerender.js も直すこと */}
        <Suspense fallback={null}>
          <PageComponent setCurrentPage={setCurrentPage} />
          {isArticle && <ArticleFooter page={currentPage} setCurrentPage={setCurrentPage} />}
        </Suspense>
      </main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
}
